import { useMemo, useState } from 'react';

import { addNarrativeRequest } from 'utilitys/narrative/narrativeTools';
import { WidgetViewMode, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { UseBaseTable } from 'hooks/useBaseTable';
import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';

import { useActivitiesPredecessorsStore } from 'store/activitiesPredecessors.store';

import { useMutationExportCompletionPlanToCsv } from 'api/mutations/exportCompletionPlanToCsv.mutation';
import { useQueryActivityCompletionPlan } from 'api/queries/activityCompletionPlan.query';

import { FilterNames, getFiltersData } from 'components/common/GlobalFilterComponent/GlobalFilterComponent.utils';

import { usePinboardWidgetWrapperContext } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/context/widgetWrapperContext';
import { TKeys } from 'components/widgets/ActivityCompletionPlan/ActivitiesPredecessors.utils';

export type UseActivitiesPredecessorsProvider = ReturnType<typeof useActivitiesPredecessorsProvider>;

interface Props {
    componentKey: string;
    widgetId?: string;
    latestVersionId?: number | undefined;
    compareVersionId?: number | undefined;
    globalFilterData: any;
    localFilters: any;
    contractId: number | null | undefined;
    table: UseBaseTable['table'];
    widgetViewMode: WidgetViewMode;
}

export const useActivitiesPredecessorsProvider = ({
    widgetId,
    latestVersionId,
    localFilters,
    globalFilterData,
    componentKey,
    contractId,
    table,
    widgetViewMode,
}: Props) => {
    const { isGenerativeAi, reportId } = usePinboardWidgetWrapperContext();
    const isInPinboard = Boolean(widgetId);

    const {
        activityTypeFilter,
        statusFilter,
        submittedCompletionFilter,
        activeTab: activeTabFromStore,
        isCriticalPath,
        isDelayDriver,
        update: updateActivitiesPredecessorsStore,
    } = useActivitiesPredecessorsStore();

    const [activeTab, onSetActiveTab] = useActiveTabState({
        widgetViewMode,
        activeTabFromStore,
        localFilters,
        updateActivitiesPredecessorsStore,
    });

    // Active tab is in both states because it is live state
    const source = isInPinboard
        ? { ...localFilters, activeTab }
        : {
              ...globalFilterData,
              activityTypeFilter,
              statusFilter,
              isCriticalPath,
              isDelayDriver,
              submittedCompletionFilter,
              activeTab,
          };

    const filters = useMemo(() => {
        return getFiltersData({
            [FilterNames.FRAGNET_SEARCH]: source?.milestoneFragnet,
            [FilterNames.ACTIVITY_TYPE_FILTER]: source.activityTypeFilter?.map((item) => item.value) || [],
            [FilterNames.OPTIONS_STATUS]: source.statusFilter?.map((item) => item.value) || [],
            [FilterNames.CRITICAL_PATH]: source.isCriticalPath ? [1] : [],
            [FilterNames.SUBMITTED_COMPLETION_CARD_BOARD]: source.submittedCompletionFilter,
            [FilterNames.DELAY_DRIVER]: source.isDelayDriver ? ['true'] : ['false'],
        });
    }, [
        source?.activityTypeFilter,
        source?.statusFilter,
        source?.isCriticalPath,
        source?.isDelayDriver,
        source?.submittedCompletionFilter,
        source?.milestoneFragnet,
    ]);

    // create pill data for PinboardWidgetPills component
    const pillsData = {
        milestoneFragnet: source?.milestoneFragnet,
        activityTypeFilter: source?.activityTypeFilter?.map((i) => i.value),
        statusFilter: source?.statusFilter?.map((i) => i.value),
        isCriticalPath: source?.isCriticalPath,
        submittedCompletionFilter: source?.submittedCompletionFilter,
        isDelayDriver: source?.isDelayDriver ? ['true'] : ['false'],
    };

    const { pillsList: pills } = useGlobalFiltersHook({ widgetId, source: pillsData, latestVersionId });

    const getActivityCompletionPlanPayload = {
        contractId,
        metaDataId: latestVersionId,
        tabId: source.activeTab,
        filter: {
            filters: filters,
        },
        sortingRequestList: table.sorting,
        paging: { ...table.pagination, page: table.pagination.page - 1 },
        ...addNarrativeRequest(isGenerativeAi, componentKey, widgetId, reportId),
    };

    return {
        ui: {
            state: {
                milestoneFragnet: source.milestoneFragnet,
                activityTypeFilter: source.activityTypeFilter,
                statusFilter: source.statusFilter,
                isCriticalPath: source.isCriticalPath,
                submittedCompletionFilter: source.submittedCompletionFilter,
                isDelayDriver: source.isDelayDriver,
                activeTab: source.activeTab,
            },
            setters: {
                onSetActiveTab,
            },
            pills,
            isInPinboard,
        },
        api: {
            exportCompletionPlanToCsv: {
                instance: useMutationExportCompletionPlanToCsv(),
                payload: {
                    params: {
                        contractId,
                        metaDataId: latestVersionId,
                        tabId: source.activeTab,
                        filter: {
                            filters: filters,
                        },
                        sortingRequestList: table.sorting,
                        paging: { ...table.pagination, page: table.pagination.page - 1 },
                    },
                },
            },
            getActivityCompletionPlan: {
                instance: useQueryActivityCompletionPlan(getActivityCompletionPlanPayload),
                payload: getActivityCompletionPlanPayload,
            },
        },
    };
};

const useActiveTabState = ({
    widgetViewMode,
    activeTabFromStore,
    localFilters,
    updateActivitiesPredecessorsStore,
}): [TKeys, (tab: TKeys) => void] => {
    // Determine initial active tab based on view mode
    // if page view: use store value, else(report any mode) use backend saved localFilters (or default to 'blocking')
    const activeTabInitialState = WidgetViewModeUtils.isPageView(widgetViewMode)
        ? activeTabFromStore
        : (localFilters?.activeTab as TKeys) || 'blocking';

    const [activeTab, setActiveTab] = useState<TKeys>(activeTabInitialState);

    // Handler to update active tab state, if page view update store as well.
    const onSetActiveTab = (tab: TKeys) => {
        if (WidgetViewModeUtils.isPageView(widgetViewMode)) {
            updateActivitiesPredecessorsStore({ activeTab: tab });
        }
        setActiveTab(tab);
    };
    return [activeTab, onSetActiveTab];
};
